<c-container fluid>
  <c-row>
    <c-col xs="12" lg="8" xl="6" class="mx-auto">
      <button cButton color="secondary" variant="ghost" (click)="navigateBack()" class="back d-flex align-items-center mb-4">
        <svg cIcon name="cilArrowLeft" size="lg" class="me-2"></svg>
        <span>{{ 'actions.back' | translate }}</span>
      </button>

      <!-- Loading State -->
      @if (loadingInfo()) {
        <div class="text-center py-5">
          <c-spinner color="primary"></c-spinner>
          <p class="text-muted mt-3">Loading payout information...</p>
        </div>
      }

      <!-- Main Content -->
      @if (!loadingInfo()) {
        <!-- Messages -->
        @if (error(); as err) {
          <c-alert color="danger" [dismissible]="true" (visibleChange)="error.set(null)">
            {{ err }}
          </c-alert>
        }

        @if (success(); as msg) {
          <c-alert color="success">
            <svg cIcon name="cilCheckCircle" class="me-2"></svg>
            {{ msg }}
          </c-alert>
        }

        <!-- Success State after confirmation -->
        @if (success() && !awaitingConfirmation()) {
          <div class="text-center py-4">
            <button cButton color="primary" (click)="navigateBack()">
              <svg cIcon name="cilArrowLeft" class="me-2"></svg>
              Back to My Payouts
            </button>
          </div>
        }

        <!-- Confirmation Code Entry (Step 2) -->
        @if (awaitingConfirmation()) {
          <c-card class="mb-4">
            <c-card-header class="bg-primary text-white">
              <h5 class="mb-0">
                <svg cIcon name="cilLockLocked" class="me-2"></svg>
                Confirm Your Payout Request
              </h5>
            </c-card-header>
            <c-card-body class="text-center py-5">
              <div class="mb-4">
                <svg cIcon name="cilEnvelopeClosed" size="3xl" class="text-primary mb-3"></svg>
                <h4>Check Your Email</h4>
                <p class="text-muted mb-4">
                  We've sent a 6-digit confirmation code to your email address.<br>
                  Please enter it below to complete your payout request.
                </p>
              </div>

              <!-- Payout Summary -->
              <div class="bg-dark p-3 rounded mb-4 mx-auto" style="max-width: 400px;">
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">Amount:</span>
                  <span class="text-white">{{ formatAmount(newPayoutAmount()) }} USD</span>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted">Fee:</span>
                  <span class="text-warning">{{ formatAmount(calculatedFee()) }} USD</span>
                </div>
                <div class="d-flex justify-content-between">
                  <span class="text-muted">You will receive:</span>
                  <span class="text-success fw-bold">{{ formatAmount(calculatedAmountSent()) }} {{ selectedToken() }}</span>
                </div>
              </div>

              <!-- Confirmation Code Input -->
              <div class="mx-auto" style="max-width: 300px;">
                <label cFormLabel class="text-start w-100">Confirmation Code *</label>
                <input
                  cFormControl
                  type="text"
                  [ngModel]="confirmationCode()"
                  (ngModelChange)="confirmationCode.set($event)"
                  placeholder="000000"
                  maxlength="6"
                  class="text-center fs-3 font-monospace letter-spacing-wide"
                  style="letter-spacing: 0.5em;"
                  [class.is-invalid]="confirmationCode().length > 0 && confirmationCode().length < 6"
                  [disabled]="confirmingPayout() || isConfirmationExpired()"
                />
                @if (confirmationExpiresAt()) {
                  <small class="text-muted d-block mt-2">
                    Code expires in: <span [class.text-danger]="isConfirmationExpired()">{{ expirationTimeRemaining() }}</span>
                  </small>
                }
              </div>

              <!-- Buttons -->
              <div class="d-flex flex-column flex-sm-row gap-3 mt-4 mx-auto" style="max-width: 400px;">
                <button
                  cButton
                  color="primary"
                  size="lg"
                  class="flex-grow-1"
                  [disabled]="confirmationCode().length !== 6 || confirmingPayout() || isConfirmationExpired()"
                  (click)="confirmPayout()"
                >
                  @if (confirmingPayout()) {
                    <c-spinner size="sm" class="me-2"></c-spinner>
                  }
                  {{ confirmingPayout() ? 'Confirming...' : 'Confirm Payout' }}
                </button>
              </div>

              <div class="mt-4">
                <button
                  cButton
                  color="link"
                  [disabled]="resendingCode()"
                  (click)="resendCode()"
                >
                  @if (resendingCode()) {
                    <c-spinner size="sm" class="me-2"></c-spinner>
                  }
                  {{ resendingCode() ? 'Sending...' : 'Resend Code' }}
                </button>
                <span class="text-muted mx-2">|</span>
                <button
                  cButton
                  color="link"
                  class="text-danger"
                  (click)="cancelConfirmation()"
                >
                  Cancel Request
                </button>
              </div>
            </c-card-body>
          </c-card>
        }

        <!-- Form Content (Step 1) -->
        @if (!success() && !awaitingConfirmation()) {
          <!-- Balance Info Card -->
          <c-card class="mb-4">
            <c-card-body>
              <c-row>
                <c-col xs="12" sm="4" class="text-center mb-3 mb-sm-0">
                  <div class="text-muted small">Available for Payout</div>
                  <div class="h4 mb-0 text-success">{{ formatAmount(payoutInfo()?.balance) }} USD</div>
                </c-col>
                <c-col xs="12" sm="4" class="text-center mb-3 mb-sm-0">
                  <div class="text-muted small">Pending Payouts</div>
                  <div class="h4 mb-0" [class.text-warning]="payoutInfo() && payoutInfo()!.pending_payout_amount > 0">
                    {{ formatAmount(payoutInfo()?.pending_payout_amount) }} USD
                  </div>
                </c-col>
                <c-col xs="12" sm="4" class="text-center">
                  <div class="text-muted small">Payout Fee</div>
                  <div class="h5 mb-0 text-warning">{{ payoutInfo()?.fee_formula }}</div>
                </c-col>
              </c-row>
            </c-card-body>
          </c-card>

          <!-- Payout Currency Section -->
          <c-card class="mb-4">
            <c-card-header>
              <h5 class="mb-0">
                <svg cIcon name="cilMoney" class="me-2"></svg>
                Payout Currency
              </h5>
            </c-card-header>
            <c-card-body>
              <div class="d-flex flex-column flex-sm-row gap-3">
                <!-- USDC (always available) -->
                <button
                  cButton
                  [color]="selectedToken() === 'USDC' ? 'primary' : 'secondary'"
                  [variant]="selectedToken() === 'USDC' ? undefined : 'outline'"
                  size="lg"
                  class="flex-grow-1"
                  (click)="selectToken('USDC')"
                >
                  <svg cIcon name="cilCheckCircle" class="me-2" *ngIf="selectedToken() === 'USDC'"></svg>
                  USDC
                </button>

                <!-- USDT (only for non-European residents) -->
                @if (canSelectUsdt()) {
                  <button
                    cButton
                    [color]="selectedToken() === 'USDT' ? 'primary' : 'secondary'"
                    [variant]="selectedToken() === 'USDT' ? undefined : 'outline'"
                    size="lg"
                    class="flex-grow-1"
                    (click)="selectToken('USDT')"
                  >
                    <svg cIcon name="cilCheckCircle" class="me-2" *ngIf="selectedToken() === 'USDT'"></svg>
                    USDT
                  </button>
                }
              </div>

              @if (isEuropean()) {
                <small class="text-muted d-block mt-3">
                  <svg cIcon name="cilInfo" class="me-1"></svg>
                  For residents of the European Economic Area (EEA), payouts are available in USDC only.
                </small>
              }

              <!-- Non-EU confirmation, required for USDT -->
              @if (usdtSelected()) {
                <c-alert color="warning" class="mt-3 mb-0">
                  <div class="form-check mb-0">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="nonEuConfirm"
                      [checked]="nonEuConfirmed()"
                      (change)="nonEuConfirmed.set($any($event.target).checked)"
                    />
                    <label class="form-check-label" for="nonEuConfirm">
                      I confirm that I am <strong>not</strong> a resident of the European Economic Area (EEA)
                      and am eligible to receive a USDT payout.
                    </label>
                  </div>
                </c-alert>
              }
            </c-card-body>
          </c-card>

          <!-- Wallet Connection Section -->
          <c-card class="mb-4">
            <c-card-header>
              <h5 class="mb-0">
                <svg cIcon name="cilWallet" class="me-2"></svg>
                Wallet Address
              </h5>
            </c-card-header>
            <c-card-body>
              <!-- Not connected -->
              @if (!web3ConnectionState().connected) {
                <!-- Mobile deep link -->
                @if (web3ConnectionState().walletConnectUri && isMobile()) {
                  <div class="text-center mb-4">
                    <p class="mb-3"><strong>Connect your mobile wallet</strong></p>
                    <button
                      cButton
                      color="primary"
                      size="lg"
                      class="mb-3 w-100"
                      (click)="openMobileWallet()"
                    >
                      <svg cIcon name="cilWallet" class="me-2"></svg>
                      Open Wallet App
                    </button>
                    <button
                      cButton
                      color="secondary"
                      size="sm"
                      (click)="copyWalletConnectUri()"
                    >
                      <svg cIcon name="cilCopy" class="me-1"></svg>
                      Copy URI
                    </button>
                  </div>
                }

                <!-- QR Code (desktop) -->
                @if (web3ConnectionState().walletConnectUri && !isMobile()) {
                  <div class="text-center mb-4">
                    <p class="mb-3"><strong>Scan with your wallet app</strong></p>
                    <div class="qr-code-container d-inline-block p-3 rounded">
                      <qrcode
                        [qrdata]="web3ConnectionState().walletConnectUri!"
                        [width]="250"
                        [errorCorrectionLevel]="'M'"
                        [margin]="2"
                      ></qrcode>
                    </div>
                    <p class="text-muted mt-3">
                      <small>Scan this QR code with MetaMask, Trust Wallet, or another compatible wallet</small>
                    </p>
                  </div>
                }

                <!-- Connect Buttons -->
                @if (!web3ConnectionState().walletConnectUri) {
                  <div class="text-center mb-4">
                    <p class="text-muted mb-4">Connect your wallet to auto-fill your address, or enter it manually below.</p>

                    @if (hasBrowserWallet()) {
                      <div class="mb-3">
                        <button
                          cButton
                          color="primary"
                          size="lg"
                          class="w-100 w-sm-auto"
                          [disabled]="connectingWallet()"
                          (click)="connectWallet(false)"
                        >
                          @if (connectingWallet()) {
                            <c-spinner size="sm" class="me-2"></c-spinner>
                          }
                          {{ connectingWallet() ? 'Connecting...' : 'Connect Browser Wallet' }}
                        </button>
                        <p class="text-muted mt-2 mb-0"><small>MetaMask, Brave Wallet, etc.</small></p>
                      </div>
                    }

                    <button
                      cButton
                      [color]="hasBrowserWallet() ? 'secondary' : 'primary'"
                      size="lg"
                      class="w-100 w-sm-auto"
                      [disabled]="connectingWallet()"
                      (click)="connectWallet(true)"
                    >
                      @if (connectingWallet()) {
                        <c-spinner size="sm" class="me-2"></c-spinner>
                      }
                      {{ connectingWallet() ? 'Connecting...' : 'Connect via QR Code' }}
                    </button>
                    <p class="text-muted mt-2 mb-0"><small>Scan with Trust Wallet, MetaMask Mobile, etc.</small></p>
                  </div>
                }
              }

              <!-- Connected -->
              @if (web3ConnectionState().connected) {
                <div class="connected-wallet-info mb-4 p-3 rounded">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="text-success">
                      <svg cIcon name="cilCheckCircle" class="me-1"></svg>
                      Wallet Connected
                    </span>
                    <button cButton color="secondary" size="sm" (click)="disconnectWallet()">
                      Disconnect
                    </button>
                  </div>
                  <p class="mb-1">
                    <strong>Address:</strong> {{ shortenAddress(web3ConnectionState().address) }}
                  </p>
                  <p class="mb-1">
                    <strong>Network:</strong> {{ getNetworkName() }}
                  </p>
                  @if (walletTokenBalance() !== null) {
                    <p class="mb-0">
                      <strong>Wallet {{ selectedToken() }} Balance:</strong> {{ formatWalletBalance(walletTokenBalance()) }} {{ selectedToken() }}
                    </p>
                  }
                  @if (!isCorrectNetwork()) {
                    <c-alert color="warning" class="mt-3 mb-0">
                      <svg cIcon name="cilWarning" class="me-2"></svg>
                      Please switch to <strong>BNB Smart Chain (BSC)</strong> network to ensure your payout is sent correctly.
                      <button cButton color="warning" size="sm" class="ms-2" (click)="switchNetwork()">
                        Switch Network
                      </button>
                    </c-alert>
                  }
                </div>
              }

              <!-- Manual Input -->
              <div class="border-top pt-4">
                <label cFormLabel>
                  {{ web3ConnectionState().connected ? 'Connected Wallet Address' : 'Or enter wallet address manually' }}
                </label>
                <input
                  cFormControl
                  type="text"
                  [ngModel]="newPayoutWalletAddress()"
                  (ngModelChange)="newPayoutWalletAddress.set($event)"
                  placeholder="0x..."
                  maxlength="42"
                  [readonly]="web3ConnectionState().connected"
                  class="font-monospace"
                />
                <small class="text-muted d-block mt-1">BSC wallet address (starts with 0x, 42 characters)</small>
                @if (newPayoutWalletAddress() && !isValidWalletAddress(newPayoutWalletAddress())) {
                  <div class="text-danger mt-2">
                    <svg cIcon name="cilWarning" class="me-1"></svg>
                    Invalid wallet address. Must start with 0x and be 42 characters.
                  </div>
                }
              </div>
            </c-card-body>
          </c-card>

          <!-- Amount Section -->
          <c-card class="mb-4">
            <c-card-header>
              <h5 class="mb-0">
                <svg cIcon name="cilDollar" class="me-2"></svg>
                Payout Amount
              </h5>
            </c-card-header>
            <c-card-body>
              <div class="mb-4">
                <label cFormLabel>Amount *</label>
                <c-input-group size="lg">
                  <input
                    cFormControl
                    type="number"
                    [ngModel]="newPayoutAmount()"
                    (ngModelChange)="newPayoutAmount.set($event); onAmountChange()"
                    [min]="payoutInfo()?.minimum_amount || 10"
                    [max]="payoutInfo()?.balance || 0"
                    step="0.01"
                    placeholder="Enter amount"
                    class="text-end"
                  />
                  <span cInputGroupText>USD</span>
                </c-input-group>
                <small class="text-muted">
                  Minimum: {{ payoutInfo()?.minimum_amount || 10 }} USD | Maximum: {{ formatAmount(payoutInfo()?.balance) }} USD
                </small>
              </div>

              <!-- Fee Calculation -->
              @if (newPayoutAmount() > 0) {
                <c-row class="mb-3">
                  <c-col xs="6">
                    <label cFormLabel class="text-muted">Fee ({{ payoutInfo()?.fee_formula }})</label>
                    <c-input-group>
                      <input
                        cFormControl
                        type="text"
                        [value]="formatAmount(calculatedFee())"
                        readonly
                        class="bg-dark text-warning text-end"
                      />
                      <span cInputGroupText>USD</span>
                    </c-input-group>
                  </c-col>
                  <c-col xs="6">
                    <label cFormLabel class="text-muted">You will receive</label>
                    <c-input-group>
                      <input
                        cFormControl
                        type="text"
                        [value]="formatAmount(calculatedAmountSent())"
                        readonly
                        class="bg-dark text-success text-end fw-bold"
                      />
                      <span cInputGroupText>{{ selectedToken() }}</span>
                    </c-input-group>
                  </c-col>
                </c-row>
              }

              <!-- Validation Messages -->
              @if (newPayoutAmount() > 0 && newPayoutAmount() > (payoutInfo()?.balance || 0)) {
                <div class="text-danger mt-2">
                  <svg cIcon name="cilWarning" class="me-1"></svg>
                  Amount exceeds your available balance.
                </div>
              }
              @if (newPayoutAmount() > 0 && calculatedAmountSent() <= 0) {
                <div class="text-danger mt-2">
                  <svg cIcon name="cilWarning" class="me-1"></svg>
                  Amount is too low to cover the fees.
                </div>
              }
            </c-card-body>
          </c-card>

          <!-- Subject (Optional) -->
          <c-card class="mb-4">
            <c-card-body>
              <label cFormLabel>Subject (optional)</label>
              <input
                cFormControl
                type="text"
                [ngModel]="newPayoutSubject()"
                (ngModelChange)="newPayoutSubject.set($event)"
                placeholder="e.g., Monthly withdrawal"
                maxlength="100"
              />
            </c-card-body>
          </c-card>

          <!-- Submit Buttons -->
          <div class="d-flex flex-column gap-3 mb-4">
            <button
              cButton
              color="primary"
              size="lg"
              [disabled]="!canCreatePayout() || creatingPayout()"
              (click)="submitPayout()"
            >
              @if (creatingPayout()) {
                <c-spinner size="sm" class="me-2"></c-spinner>
              }
              {{ creatingPayout() ? 'Creating...' : 'Request Payout' }}
            </button>

            <button
              cButton
              color="secondary"
              size="lg"
              [disabled]="creatingPayout()"
              (click)="navigateBack()"
            >
              Cancel
            </button>
          </div>
        }
      }
    </c-col>
  </c-row>
</c-container>